import React, { createContext, useContext, ReactNode } from 'react';
import BPToast, { ToastVariant } from 'components/BionicUI/toast/BPToast';
import { Transaction } from 'components/BionicUI/toast//TransactionLink';
import useNotifyStore from 'stores/notifyStore';

type Notify = (
  message: string,
  description?: string | null,
  transaction?: Transaction | null,
  variant?: ToastVariant | null
) => void;

type NotifyError = (
  title: string,
  description?: string | null
) => Promise<void>;

type NotifySuccess = (
  message: string,
  description?: string | null,
  transaction?: Transaction | null
) => Promise<void>;

type NotifyBlueSpinner = (
  message: string,
  description?: string | null,
  transaction?: Transaction | null
) => Promise<void>;

type NotifyOrangeSpinner = (
  message: string,
  description?: string | null,
  transaction?: Transaction | null
) => Promise<void>;

type NotifyContextType = {
  notify: Notify;
  notifyError: NotifyError;
  notifySuccess: NotifySuccess;
  notifyBlueSpinner: NotifyBlueSpinner;
  notifyOrangeSpinner: NotifyOrangeSpinner;
};

const NotifyContext = createContext<NotifyContextType | undefined>(undefined);

export const useNotify = (): NotifyContextType => {
  const context = useContext(NotifyContext);
  if (!context) {
    throw new Error('useNotify must be used within a NotifyProvider');
  }
  return context;
};

export const NotifyProvider = ({ children }: { children: ReactNode }) => {
  const { setCurrentToast } = useNotifyStore();

  async function notify(
    message: string,
    description?: string | null,
    transaction?: Transaction | null,
    variant?: ToastVariant | null
  ): Promise<void> {
    if (variant === null || variant === undefined) {
      variant = 'spinner_orange';
    }

    setCurrentToast(
      <BPToast
        message={message}
        description={description ? description : undefined}
        variant={variant}
        transaction={transaction ? transaction : null}
      />
    );
  }

  async function notifyError(
    title: string,
    description?: string | null
  ): Promise<void> {
    await notify(title, description, null, 'error');
  }

  async function notifySuccess(
    message: string,
    description?: string | null,
    transaction?: Transaction | null
  ): Promise<void> {
    await notify(message, description, transaction, 'success');
  }

  async function notifyOrangeSpinner(
    message: string,
    description?: string | null,
    transaction?: Transaction | null
  ): Promise<void> {
    await notify(message, description, transaction, 'spinner_orange');
  }

  async function notifyBlueSpinner(
    message: string,
    description?: string | null,
    transaction?: Transaction | null
  ): Promise<void> {
    await notify(message, description, transaction, 'spinner_blue');
  }

  return (
    <NotifyContext.Provider
      value={{
        notify,
        notifyError,
        notifySuccess,
        notifyBlueSpinner,
        notifyOrangeSpinner,
      }}
    >
      {children}
    </NotifyContext.Provider>
  );
};
